import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import React, {useEffect, useMemo, useState} from 'react';
import {ListConfigContext} from '@src/contexts/ListConfigContext';
import {ListConfigNamedColumn, ListPaginationHttpResponse, ListSortSettings} from '@src/@types/components/List';
import app from '../../helpers/app';
import {UserPreferencesKeys} from '@src/@types/services/UserPreferencesNamesResolver';
import ListContent from '../List/ListContent';
import {useQuery} from '@tanstack/react-query';
import {ControlledListProps} from '@src/@types/components/ControlledList';
import classnames from '../../helpers/classnames';
import {IListConfigContext} from '@src/@interfaces/contexts/IListConfigContext';

const emptyObject = {};
const returnFalse = () => false;
const voidFunction = () => void 0;

const fakeResponse = {
    data: [],
    current_page: 1,
    first_page_url: '',
    last_page: 1,
    last_page_url: '',
    links: [],
    per_page: 1,
    total: 1,
};

export default function ControlledList<Model extends IEmptyModel>(props: ControlledListProps<Model>) {
    const configTitleField = '';

    const [searchTerm, setSearchTerm] = useState<string | null | undefined>();
    const [sortBy, setSortBy] = useState<ListSortSettings | undefined>();

    const {uniqueListName, controllerName, listContextValues, config: rawConfig, columns} = props;
    const additionalColumnsNumber = React.Children.count(props.listContentProps?.tableHeadProps?.cellBeforeColumns);
    const [config, configContextValue, customRefetch] = useMemo(() => {
        const mapOfColumns = columns.reduce<Record<string, ListConfigNamedColumn>>((res, item) => {
            res[item.name] = item;

            return res;
        }, {});
        const config = {
            ...(rawConfig ?? emptyObject),
            title: configTitleField,
            columns: mapOfColumns,
        };

        const interactiveConfig = app('config-maker').makeListConfig(uniqueListName, config);
        const user = app('auth').getUser();

        const getPreparedColumns = () => interactiveConfig.getPreparedColumnsList(
                mapOfColumns,
                user?.getModelPreferenceValue(uniqueListName, UserPreferencesKeys.LIST_COLUMNS_VISIBILITY),
                user?.getModelPreferenceValue(uniqueListName, UserPreferencesKeys.LIST_COLUMNS_ORDER)
        );

        let preparedColumns = getPreparedColumns();

        return [
            config,
            {
                ...(listContextValues ?? emptyObject),
                controllerName: controllerName ?? uniqueListName,
                config,
                get columns() {
                    return preparedColumns;
                },
                columnsNumber: preparedColumns.length + 1 + additionalColumnsNumber,
                currentPage: 1,
                setCurrentPage: voidFunction,
            } as IListConfigContext,
            () => {
                preparedColumns = getPreparedColumns();
            },
        ];
    }, [controllerName, additionalColumnsNumber, uniqueListName, columns, rawConfig, listContextValues]);

    useEffect(() => () => {
        app('config-maker').forgetListConfigCache(uniqueListName, config);
    }, [uniqueListName, config]);

    let records = props.records;
    if (props.onSearch) {
        records = props.onSearch(records, searchTerm);
    }
    if (props.onSort) {
        records = props.onSort(records, sortBy);
    }

    const customQueryResult = props.queryResult;

    const queryKey = app('queryKeys').getFakeRequestQueryKey(
            'custom-list-of-records',
            uniqueListName,
            props.queryKeyTags
    );

    const queryResult = useQuery<ListPaginationHttpResponse<Model>, Error>(
            queryKey,
            () => fakeResponse,
            {
                useErrorBoundary: false,
                cacheTime: 0,
                staleTime: 0,
                refetchOnWindowFocus: false,
                refetchInterval: false,
                refetchIntervalInBackground: false,
                retry: false,
                refetchOnReconnect: false,
            }
    );

    const Wrapper = props.bordered ? 'div' : React.Fragment;
    const wrapperProps = props.bordered ? {
        className: classnames(props.className, {'border rounded-2': props.bordered})
    } : {};

    return <Wrapper {...wrapperProps}>
        <ListConfigContext.Provider value={configContextValue}>
            <ListContent
                    canOpenModelShowPage={returnFalse}
                    uniqueListName={uniqueListName}
                    listSettingsControllerName={uniqueListName}
                    pageNumberGetParamName={`${uniqueListName}--page-number`}
                    withoutPagination={true}
                    withoutSearchField={props.withoutSearchField}
                    keepWrapperInBox={props.keepInParentBox}
                    {...(props.listContentProps ?? emptyObject)}
                    queryResult={customQueryResult ?? queryResult}
                    listOfRecords={records}
                    searchTerm={searchTerm}
                    saveSearchTerm={setSearchTerm}
                    sortBy={sortBy}
                    saveSortingParams={setSortBy}
                    customRefetch={customRefetch}
                    customPaginationData={fakeResponse}
                    withoutListSettings={props.withoutSettings}
                    isLoading={props.isLoading}
            />
        </ListConfigContext.Provider>
    </Wrapper>;
}
